import { isDefined, isNotDefined } from '@sgme/fp';
import { Button, HStack, Spacer, VStack } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { type ReactNode, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { TimeProgress } from '@/App/tiles/shared/TimeProgress.tsx';
import { SgButton } from '@/components/sg-design-system/components/input/SgButton';
import { checkIfUserInternalSales, useUserContext } from '@/context/UserContext.tsx';
import { useCancelCashRfsMutation, useRequestCashRfsMutation } from '@/store/cash/api/index.ts';
import type { RequestCashRfsParams } from '@/store/cash/api/requestCashRfs.ts';
import { getRequestCashRfsMutationCacheKey } from '@/store/cash/selectors/rfs.ts';
import { useAppSelector } from '@/store/hooks.ts';
import { getIsStreamingConnected } from '@/store/streaming/slice.ts';
import { getNewRfsId } from '@/utils/rfs/getNewRfsId.ts';

export type CashTileFooterProps = {
  tileId: string;
  /** Whether the footer should be visible */
  isVisible: boolean;
  /** Whether RFS is priceable (ready to request) */
  isPriceable: boolean;
  /** RFS request parameters */
  rfsRequestParams: RequestCashRfsParams | undefined;
  /** Current RFS ID */
  rfsId: string | undefined;
  /** Indicates if an RFS (Request For Stream) is currently pending */
  isRfsPending: boolean;
  /** Remaining time in seconds */
  remainingTime: number | undefined;
  /** Total RFS window duration in seconds */
  totalTime: number | undefined;
  /** Trader name */
  traderName: string | undefined;
  /** Additional controls to render before the trader name button (e.g., timestamp button, negotiation counter) */
  additionalControls?: ReactNode;
};

/**
 * CashTileFooter - Unified footer component for all Cash tiles (Spot/Forward/NDF)
 * Handles RFS request/cancel logic and displays pricing status with modern progress bar design
 *
 * @example
 * // For Spot (simple)
 * <CashTileFooter
 *   tileId={tileId}
 *   isVisible={isRfsRequired}
 *   isPriceable={isPriceable}
 *   rfsRequestParams={rfsRequestParams}
 *   rfsId={rfsId}
 *   remainingTime={remainingTime}
 *   totalTime={totalTime}
 *   traderName={traderName}
 * />
 *
 * @example
 * // For Forward (with additional controls)
 * <CashTileFooter
 *   tileId={tileId}
 *   isVisible={isVisible}
 *   isPriceable={isPriceable}
 *   rfsRequestParams={rfsRequestParams}
 *   rfsId={rfsId}
 *   remainingTime={remainingTime}
 *   totalTime={totalTime}
 *   traderName={traderName}
 *   additionalControls={
 *     <>
 *       <Button onClick={handleTimestamp}>Timestamp</Button>
 *       <NegotiationCounter />
 *     </>
 *   }
 * />
 */
export function CashTileFooter(props: CashTileFooterProps) {
  const {
    tileId,
    isVisible,
    isPriceable,
    rfsRequestParams,
    rfsId,
    isRfsPending,
    remainingTime,
    totalTime,
    traderName,
    additionalControls,
  } = props;

  const { sgwtWebAnalytics } = useSgwtWidgets();
  const isSignalRConnected = useAppSelector(getIsStreamingConnected);

  const userContextValue = useUserContext();
  const isInternalSales = checkIfUserInternalSales(userContextValue);

  const [requestCashRfs, { isLoading: isRfsRequestLoading }] = useRequestCashRfsMutation({
    fixedCacheKey: getRequestCashRfsMutationCacheKey(tileId),
  });
  const [requestCashRfsCancellation] = useCancelCashRfsMutation();

  const handleRequestRfs = useCallback(async () => {
    if (isNotDefined(rfsRequestParams)) {
      return;
    }

    sgwtWebAnalytics?.trackEvent('Cash', 'RFS requested');

    await requestCashRfs({ ...rfsRequestParams, rfsId: getNewRfsId() });
  }, [rfsRequestParams, requestCashRfs, sgwtWebAnalytics]);

  const cancelRfs = useCallback(() => {
    if (isNotDefined(rfsId)) {
      return;
    }

    sgwtWebAnalytics?.trackEvent('Cash', 'RFS canceled');

    requestCashRfsCancellation({ tileId, rfsId });
  }, [tileId, rfsId, requestCashRfsCancellation, sgwtWebAnalytics]);

  if (!isVisible) {
    return null;
  }

  if (isNotDefined(remainingTime) && !isRfsPending) {
    return (
      <div className="p-2">
        <SgButton
          tabIndex={-1}
          type="button"
          size="sm"
          purpose="primary"
          color="info"
          onClick={handleRequestRfs}
          disabled={!isPriceable || !isSignalRConnected || isRfsRequestLoading || isRfsPending}
          block
          data-e2e="price-rfs-button"
        >
          <FormattedMessage id="CashTile.footer.Price" />
        </SgButton>
      </div>
    );
  }

  return (
    <VStack className="px-2 pt-3" gap="8px">
      <div>
        <TimeProgress totalTime={totalTime} remainingTime={remainingTime} onCancel={cancelRfs} />
      </div>

      {isInternalSales && (
        <HStack fullWidth gap="8px">
          {additionalControls}

          <Spacer />

          {isDefined(traderName) && (
            <Button size="sm" data-e2e="trader-name-button">
              {traderName}
            </Button>
          )}
        </HStack>
      )}
    </VStack>
  );
}
